import { AnyAction } from 'redux';
import { ApplicationState } from 'app/types-store';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { mapMetadataToStore } from 'app/area/import-mappings/map-metadata-to-store';
import { STATE } from 'app/constants';
import { ActionDispatching, ComplexValue, LoadOptions } from 'app/types-shared';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess } from 'app/area/shared/actions-utils';
import { FilterSearchModel } from 'app/area/search-filters/types-store';
import { codeFieldIncludeInactive, defaultMaxResults, referenceCodetableId, importMappingsTranslation as text } from './constants';
import { api } from './api';
import { mapListToStore } from './map-list-to-store';
import {
  RequestCodeItems, ReceiveCodeItems, FailCodeItems, ExpandCodeItem, UpdateSearchModel,
  RequestCodetableMetadata, ReceiveCodetableMetadata, FailCodetableMetadata,
  ClearList, ModifyItem,
  ValidationChange, SetCurrentCode, ClearCurrentCode, UpdateCodetable,
  UpdateImportSet, UpdateCategory, RequestImportSetData, ReceiveImportSetData, FailImportSetData,
  RequestCodetableData, ReceiveCodetableData, FailCodetableData,
  ClearAll, ClearMetadata, ClearValidation, RequestCategoryOptions, ReceiveCategoryOptions, FailCategoryOptions,
  ReceiveSaveItemChanges, FailSaveItemChanges, RequestSaveItemChanges, UpdateCodeListBranch
} from './action-types';
import { CodeFieldWireKey, ModifiedImportMappings, CodeListItem, ImportMappingType } from './types-store';
import { defaultIncludeInactiveCodes } from './constants';
import { LoadCodeItemsPayload } from './types-store';
import { LoadCodeItemsParams } from 'app/area/import-mappings/types';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { mapCategoryListToStore, mapCurrentOptions } from 'app/area/import-mappings/map-to-store';

export const actionCreators = {

  saveItemChanges: (
    importSetId: number,
    codetableId: number,
    codeId: number,
    importNumber: number,
    type: ImportMappingType,
    externalValue: string,
    existingExternalValue: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      try {
        dispatch(new RequestSaveItemChanges());

        const request = {
          [ImportMappingType.NewUpdated]: () => api.addExternalValue(
            importSetId, codetableId, codeId, externalValue
          ),
          [ImportMappingType.Updated]: () => api.updateExternalValue(
            importSetId, codetableId, codeId, importNumber, externalValue
          ),
          [ImportMappingType.Deleted]: () => api.deleteExternalValue(
            importSetId, codetableId, codeId, importNumber
          )
        }[type];

        const result = await request();

        dispatch(new ReceiveSaveItemChanges(result?.data ? result?.data : importNumber));
        dispatch(new UpdateCodeListBranch());
      } catch (err) {
        dispatch(new FailSaveItemChanges());

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          const newValidationErrors = {
            importCode: err.validationErrors.importCode.map(error => {
              return { ...error, parameters: { importNumber, codetableId, codeId, value: externalValue, existingExternalValue } };
            })
          };
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new ValidationChange(complexValidationFromServer(newValidationErrors)));
        }
      }
    },

  updateSearchModel: (model: FilterSearchModel): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new UpdateSearchModel(model));
    },

  updateCodetable: (value: ComplexValue<number>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateCodetable(value));
    },

  updateCategory: (value: ComplexValue<number>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateCategory(value));
    },

  updateImportSet: (value: ComplexValue<number>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateImportSet(value));
    },

  loadCodeItems: (payload: LoadCodeItemsPayload): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      const { importSetId, codetableId, parentId, categoryId, startIndex = 0, maxResults = defaultMaxResults,
        includeInactiveCodes = codeFieldIncludeInactive[defaultIncludeInactiveCodes],
        searchModel } = payload;

      dispatch(new RequestCodeItems(codetableId, parentId, categoryId, searchModel));

      try {
        const ignoreCategoryFilters = payload.codetableId === referenceCodetableId;
        const codeItemsResponse = await api.loadCodeItems({
          importSetId,
          id: codetableId,
          parentId,
          categoryId,
          startIndex,
          maxResults,
          model: searchModel,
          displayNotSelected: true,
          limitFieldsTo: [CodeFieldWireKey.code, CodeFieldWireKey.category],
          includeInactiveCodes,
          ...(ignoreCategoryFilters && { ignoreCategoryFilters })
        });

        const codeItems = mapListToStore(codetableId, codeItemsResponse.results);
        const numberOfResults = codeItemsResponse.totalNumberOfResults;
        const clearList = startIndex === 0;

        dispatch(new ReceiveCodeItems(codetableId, parentId, categoryId, searchModel, codeItems, numberOfResults, clearList));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.importMappings,
            translationId: text.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodeItems(codetableId, parentId, categoryId, searchModel));
      }
    },

  loadImportSetData: (id: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestImportSetData());

      try {
        const wire = await api.loadImportSetOptions();
        const data = wire
          .map(t => { return { value: t.v, description: t.d } })
          .find(item => item.value === id);

        dispatch(new ReceiveImportSetData(data));

      } catch (err) {
        dispatch(new FailImportSetData());
      }
    },

  loadCodetableData: (id: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCodetableData());

      try {
        const wire = await api.loadCodetableOptions();
        const data = wire
          .map(t => { return { value: t.v, description: t.d } })
          .find(item => item.value === id);

        dispatch(new ReceiveCodetableData(data));

      } catch (err) {
        dispatch(new FailCodetableData());
      }
    },

  loadCodetableMetadata: (id: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCodetableMetadata());

      try {
        const metadataWire = await api.loadMetadata(id);
        const metadata = mapMetadataToStore(metadataWire);

        dispatch(new ReceiveCodetableMetadata(metadata));

      } catch (err) {
        dispatch(new FailCodetableMetadata());
      }
    },

  loadCategoryOptions: (
    id: string,
    codetableId: number,
    loadOptionsParams: LoadOptions,
    optionsParams?: LoadCodeItemsParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCategoryOptions(id, loadOptionsParams));

      try {
        const parentId = loadOptionsParams && loadOptionsParams.parentId || undefined;
        const siblingId = loadOptionsParams && loadOptionsParams.siblingId || undefined;
        const code = loadOptionsParams && loadOptionsParams.code;
        const query = loadOptionsParams && loadOptionsParams.query || undefined;
        const includeDisabled = loadOptionsParams && loadOptionsParams.includeDisabled;
        const displayNotSelected = optionsParams && optionsParams.displayNotSelected;

        const [codes, path] = await Promise.all([
          api.loadCategoryOptions({
            id: codetableId,
            parentId,
            siblingId,
            query,
            categoryId: loadOptionsParams && loadOptionsParams.categoryId,
            limitFieldsTo: optionsParams?.limitFieldsTo ?? ['code'],
            ...(displayNotSelected !== undefined && { displayNotSelected }),
            includeDisabled: !!includeDisabled,
            ...(code !== undefined && { code }),
            ...(query !== undefined && { returnLeavesOnly: true })
          }),
          siblingId !== undefined || parentId !== undefined
            ? api.loadPath(codetableId, siblingId || parentId)
            : Promise.resolve(undefined)]);

        const wireCodes = codes && codes.results || [];
        const pathCodes = codeItemToStoreValue(path || {});

        const storeCodes = mapCategoryListToStore(wireCodes, pathCodes);
        const currentOptions = mapCurrentOptions(wireCodes);

        dispatch(new ReceiveCategoryOptions(id, loadOptionsParams, storeCodes, currentOptions));
      } catch (err) {

        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
        dispatch(new FailCategoryOptions(id, loadOptionsParams));
      }
    },

  expandCodeItem: (parentId: number): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ExpandCodeItem(parentId));
    },

  modifyItem: (
    modifiedData: ModifiedImportMappings
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ModifyItem(modifiedData));
    },

  setCurrentCode: (
    data: CodeListItem
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new SetCurrentCode(data));
    },

  clearList: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearList());
    },

  clearMetadata: (name: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearMetadata(name));
    },

  clearValidation: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearValidation());
    },

  clearAll: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearAll());
    },

  clearCurrentCode: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearCurrentCode());
    }
};
